import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Loader2, Megaphone } from "lucide-react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";

import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@/components/ui/dialog";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { enqueueManualNotification } from "@/lib/api";
import { getErrorMessage } from "@/lib/error-messages";

const sessionMessageSchema = z.object({
    subject: z.string().min(1, "Betreff ist erforderlich"),
    body: z.string().min(1, "Text ist erforderlich"),
});

type SessionMessageValues = z.infer<typeof sessionMessageSchema>;

interface SessionMessageDialogProps {
    sessionId: string;
    sessionName?: string;
    trigger?: React.ReactNode;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
}

export function SessionMessageDialog({
    sessionId,
    sessionName,
    trigger,
    open: externalOpen,
    onOpenChange: externalOnOpenChange,
}: SessionMessageDialogProps) {
    const [internalOpen, setInternalOpen] = useState(false);
    const open = externalOpen !== undefined ? externalOpen : internalOpen;
    const setOpen = externalOnOpenChange !== undefined ? externalOnOpenChange : setInternalOpen;

    const form = useForm<SessionMessageValues>({
        resolver: zodResolver(sessionMessageSchema),
        defaultValues: {
            subject: "",
            body: "",
        },
    });

    const sendMutation = useMutation({
        mutationFn: (values: SessionMessageValues) =>
            enqueueManualNotification({
                templateSlug: "manual",
                sessionId: sessionId,
                customSubject: values.subject,
                customBody: values.body,
                isBulk: true,
            }),
        onSuccess: () => {
            toast.success("Nachricht wurde an alle Teilnehmer versendet.");
            form.reset();
            setOpen(false);
        },
        onError: (error) => toast.error(getErrorMessage(error)),
    });

    return (
        <Dialog open={open} onOpenChange={setOpen}>
            {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Megaphone className="h-5 w-5 text-primary" />
                        Nachricht an Teilnehmer senden
                    </DialogTitle>
                    <DialogDescription>
                        {sessionName ? `Kurs: ${sessionName}` : "Alle angemeldeten Teilnehmer erhalten diese Nachricht per E-Mail."}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(v => sendMutation.mutate(v))} className="space-y-6 py-4">
                        <FormField
                            control={form.control}
                            name="subject"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Betreff</FormLabel>
                                    <FormControl>
                                        <Input placeholder="Betreff der Nachricht" {...field} />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="body"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>Text</FormLabel>
                                    <FormControl>
                                        <Textarea
                                            placeholder="Deine Nachricht an die Teilnehmer..."
                                            className="min-h-[200px]"
                                            {...field}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                                Abbrechen
                            </Button>
                            <Button type="submit" disabled={sendMutation.isPending}>
                                {sendMutation.isPending ? (
                                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sende...</>
                                ) : (
                                    <><Send className="mr-2 h-4 w-4" /> Nachricht versenden</>
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
